import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollTo } from "./primitives";

const HERO_IMG =
    "https://images.unsplash.com/photo-1560521608-b4e1acca0824?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const line = {
    hidden: { y: "112%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 1.1, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Hero() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const typeY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

    return (
        <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen pt-16">
            <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 pb-10 pt-14 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:pt-20">
                {/* kinetic type block */}
                <motion.div style={reduce ? undefined : { y: typeY }} className="flex flex-col justify-between">
                    <div>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="mb-8 flex items-center gap-3"
                        >
                            <span className="h-[2px] w-10 bg-orange" />
                            <span className="text-[11px] font-semibold uppercase tracking-wide2 text-navy/70">
                                Railway Civil Works Contractor — Jodhpur, Rajasthan
                            </span>
                        </motion.div>

                        <h1 className="font-display text-[15vw] font-bold uppercase leading-[0.92] tracking-mega text-navy sm:text-[11vw] lg:text-[7.2rem]">
                            <span className="block overflow-hidden">
                                <motion.span custom={0} variants={line} initial="hidden" animate={reduce ? "show" : "show"} className="block will-change-transform">
                                    Building
                                </motion.span>
                            </span>
                            <span className="block overflow-hidden">
                                <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block will-change-transform">
                                    The Rails
                                </motion.span>
                            </span>
                            <span className="block overflow-hidden">
                                <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-orange will-change-transform">
                                    That Move
                                </motion.span>
                            </span>
                            <span className="block overflow-hidden">
                                <motion.span custom={3} variants={line} initial="hidden" animate="show" className="block will-change-transform">
                                    India
                                </motion.span>
                            </span>
                        </h1>
                    </div>

                    <div className="mt-10 max-w-md">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.9 }}
                            className="text-base leading-relaxed text-ink/70"
                        >
                            Pawan Putra Infra executes railway civil works and general
                            infrastructure — track works, platforms, bridges and earthwork —
                            engineered for precision, delivered on schedule.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.05 }}
                            className="mt-8 flex flex-wrap items-center gap-4"
                        >
                            <motion.button
                                data-testid="hero-cta-button"
                                onClick={() => scrollTo("#projects")}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                className="bg-navy px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-orange hover:text-navy-deep"
                            >
                                View Our Work
                            </motion.button>
                            <button
                                data-testid="hero-contact-button"
                                onClick={() => scrollTo("#contact")}
                                className="link-sweep font-display text-sm font-semibold uppercase tracking-[0.14em] text-navy"
                            >
                                Get In Touch
                            </button>
                        </motion.div>
                    </div>
                </motion.div>

                {/* clipped, parallaxed image */}
                <motion.div
                    initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
                    animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
                    transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative hidden lg:block"
                >
                    <div className="clip-tag relative h-[74vh] overflow-hidden border border-navy-line">
                        <motion.img
                            src={HERO_IMG}
                            alt="Train arriving at a railway station platform"
                            style={reduce ? undefined : { y: imgY, scale: 1.18 }}
                            className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-navy/10" />
                    </div>
                    <div className="absolute -bottom-5 -left-6 bg-orange px-5 py-3">
                        <span className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-navy-deep">
                            Est. Jodhpur
                        </span>
                    </div>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block"
            >
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
                    <ArrowDown className="h-5 w-5 text-navy/50" />
                </motion.div>
            </motion.div>
        </section>
    );
}
